/**
 * ColorPick Eyedropper — pick colours off any web page
 *
 * Declares one web-accessible-resource group listing nine explicit image files (icons,
 * palette/history/search glyphs, the crosshair and game sprites), matched to `<all_urls>`
 * and file URLs with no `use_dynamic_url` — so each is fetchable from any page at a fixed
 * URL.
 *
 * All the resources are images, so `probeImage` loads them with no CORS grant, surviving
 * a restrictive page CSP. A successful load is conclusive — these files are served only
 * when the extension is installed.
 *
 * Manifest verified against version 0.0.3.3.
 */

import { probeImage } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ohcpnigalekghcmgcdcenkpelffpdolg';

export const colorpickEyedropper: ExtensionDefinition = {
  id: ID,
  name: 'ColorPick Eyedropper',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 800_000,
  notes:
    'Nine explicit image WAR on <all_urls> with no dynamic URL. All load via <img> with ' +
    'no CORS grant, so detection is conclusive and survives a strict page CSP.',
  signals: [
    {
      id: 'war-icon-64',
      kind: 'web-accessible-resource',
      description:
        'Load img/icon64.png via <img> — exposed to every origin and readable without ' +
        'a CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'img/icon64.png'),
    },
    {
      id: 'war-palette-icon',
      kind: 'web-accessible-resource',
      description:
        'Load img/icons/palette/icon32.png via <img> — a nested, vendor-specific glyph ' +
        'that corroborates the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'img/icons/palette/icon32.png'),
    },
    {
      id: 'war-crosshair',
      kind: 'web-accessible-resource',
      description:
        'Load img/crosshair.png via <img> — the eyedropper crosshair sprite, a third ' +
        'independent image path.',
      weight: 0.5,
      run: () => probeImage(ID, 'img/crosshair.png'),
    },
  ],
};
